import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, LogOut, RefreshCw, Mail, BookOpen, Heart, Lock, Users } from "lucide-react";
import { GoldBadge, Reveal } from "@/components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "messages", label: "Survivor Messages", icon: Mail, testId: "admin-tab-messages" },
  { id: "preorders", label: "Pre-Orders", icon: BookOpen, testId: "admin-tab-preorders" },
  { id: "donations", label: "Donations", icon: Heart, testId: "admin-tab-donations" },
  { id: "volunteers", label: "Volunteers", icon: Users, testId: "admin-tab-volunteers" },
];

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const inputCls =
  "w-full rounded-md border border-white/15 bg-[#0A0D14]/80 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/40 transition-colors";

function VolunteerCard({ v }) {
  return (
    <div className="card-pain p-5" data-testid={`admin-volunteer-${v.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-slate-100 text-sm">{v.name}</span>
        <span className="font-code text-xs text-slate-500">{fmt(v.timestamp)}</span>
      </div>
      <p className="mt-1 text-xs text-amber-400/90 font-code uppercase tracking-wider">{v.branch}</p>
      <p className="mt-1 text-xs text-slate-400">{v.email}{v.phone ? ` — ${v.phone}` : ""}</p>
      {v.message && (
        <p className="mt-3 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{v.message}</p>
      )}
    </div>
  );
}

function MessageCard({ m }) {
  const [read, setRead] = useState(m.read === true);
  const toggle = async () => {
    const next = !read;
    setRead(next);
    try {
      await axios.patch(
        `${API}/admin/messages/${m.id}/read`,
        { read: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem("pain_admin_token")}` } }
      );
    } catch {
      setRead(!next);
    }
  };
  return (
    <div
      className={`card-pain p-5 ${read ? "opacity-70" : "border-amber-500/50"}`}
      data-testid={`admin-message-${m.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          {!read && (
            <span className="text-[10px] font-code uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/50">
              New
            </span>
          )}
          <span className="font-semibold text-slate-100 text-sm">{m.name}</span>
          {m.anonymous && (
            <span className="text-[10px] font-code uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
              Anonymous
            </span>
          )}
        </div>
        <span className="font-code text-xs text-slate-500">{fmt(m.timestamp)}</span>
      </div>
      <p className="mt-1 text-xs text-amber-400/90 font-code uppercase tracking-wider">{m.category}</p>
      {m.email && <p className="mt-1 text-xs text-slate-400">{m.email}</p>}
      <p className="mt-3 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
      <button
        data-testid={`admin-message-read-toggle-${m.id}`}
        onClick={toggle}
        className="mt-4 text-xs font-semibold uppercase tracking-wider text-amber-400/90 hover:text-amber-300 transition-colors"
      >
        {read ? "Mark as unread" : "Mark as read"}
      </button>
    </div>
  );
}

function PreorderCard({ p }) {
  return (
    <div className="card-pain p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-preorder-${p.id}`}>
      <div>
        <p className="font-semibold text-slate-100 text-sm">{p.name}</p>
        <p className="text-xs text-slate-400">{p.email}</p>
        <p className="mt-1 font-code text-xs text-slate-500">{fmt(p.timestamp)}</p>
      </div>
      <span className="font-display text-xl font-bold gold-text-gradient">
        {p.quantity} {p.quantity === 1 ? "copy" : "copies"}
      </span>
    </div>
  );
}

function DonationCard({ d }) {
  const paid = d.payment_status === "paid";
  return (
    <div className="card-pain p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-donation-${d.session_id}`}>
      <div>
        <p className="font-semibold text-slate-100 text-sm">
          {d.currency?.toUpperCase()} ${d.amount?.toFixed(2)}
        </p>
        <p className="font-code text-xs text-slate-500">{fmt(d.created_at)}</p>
      </div>
      <span
        className={`text-[10px] font-code uppercase tracking-wider px-2 py-1 rounded-full border ${
          paid
            ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
            : "bg-white/5 text-slate-400 border-white/15"
        }`}
      >
        {d.payment_status}
      </span>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("pain_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("messages");
  const [data, setData] = useState({ messages: [], preorders: [], donations: [], volunteers: [] });

  const loadAll = useCallback(
    async (t) => {
      try {
        const results = await Promise.all(
          TABS.map((tb) =>
            axios.get(`${API}/admin/${tb.id}`, { headers: { Authorization: `Bearer ${t}` } })
          )
        );
        setData({
          messages: results[0].data,
          preorders: results[1].data,
          donations: results[2].data,
          volunteers: results[3].data,
        });
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("pain_admin_token");
          setToken(null);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadAll(token);
  }, [token, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("pain_admin_token", res.data.access_token);
      setToken(res.data.access_token);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("pain_admin_token");
    setToken(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="max-w-md mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <Reveal>
          <div className="card-pain p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center h-11 w-11 rounded-lg bg-[#1D4ED8]/20 border border-amber-500/25">
                <Lock className="h-5 w-5 text-amber-400" />
              </span>
              <h1 className="font-display text-2xl font-semibold text-slate-50">Admin Inbox</h1>
            </div>
            <p className="text-sm text-slate-400 mb-8">
              Restricted access. Authorised P.A.I.N. administrators only.
            </p>
            <form data-testid="admin-login-form" onSubmit={login} className="space-y-4">
              <input
                type="email"
                data-testid="admin-email-input"
                className={inputCls}
                placeholder="Admin email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                type="password"
                data-testid="admin-password-input"
                className={inputCls}
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && (
                <p data-testid="admin-login-error" className="text-sm text-red-400">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-button"
                disabled={loading}
                className="btn-royal w-full rounded-md px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Sign In Securely"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    );
  }

  const rows = data[tab];
  const paidTotal = data.donations
    .filter((d) => d.payment_status === "paid")
    .reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <GoldBadge testId="admin-badge">Restricted — Admin Only</GoldBadge>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-50 flex items-center gap-3">
            <Shield className="h-8 w-8 text-amber-400" />
            P.A.I.N. Admin Inbox
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {(tab === "preorders" || tab === "volunteers") && (
            <button
              data-testid={`admin-export-${tab}-button`}
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/admin/${tab}/export`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("pain_admin_token")}` },
                    responseType: "blob",
                  });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = tab === "preorders" ? "born-from-broken-preorders.csv" : "pain-volunteers.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("Could not export right now.");
                }
              }}
              className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-amber-300"
            >
              <BookOpen className="h-4 w-4" />
              Export {tab === "preorders" ? "Pre-Orders" : "Volunteers"}
            </button>
          )}
          <button
            data-testid="admin-refresh-button"
            onClick={() => loadAll(token)}
            className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-amber-300"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-slate-300 border border-white/15 hover:bg-white/5 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log Out
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {TABS.map((t, i) => (
          <div key={t.id} className="card-pain p-4 sm:p-5 text-center" data-testid={`admin-stat-${t.id}`}>
            <p className="font-display text-2xl sm:text-3xl font-bold gold-text-gradient">
              {t.id === "donations" ? `$${paidTotal.toFixed(0)}` : data[t.id].length}
            </p>
            <p className="mt-1 text-[11px] sm:text-xs text-slate-400 uppercase tracking-wider">
              {t.id === "donations" ? "Donated (paid)" : t.label}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={t.testId}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.id
                ? "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                : "text-slate-400 border border-white/10 hover:bg-white/5"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            <span className="font-code text-xs opacity-70">({data[t.id].length})</span>
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4" data-testid="admin-list">
        {rows.length === 0 && (
          <p className="text-sm text-slate-500 py-10 text-center" data-testid="admin-empty-state">
            Nothing here yet.
          </p>
        )}
        {tab === "messages" && rows.map((m) => <MessageCard key={m.id} m={m} />)}
        {tab === "preorders" && rows.map((p) => <PreorderCard key={p.id} p={p} />)}
        {tab === "donations" && rows.map((d) => <DonationCard key={d.session_id} d={d} />)}
        {tab === "volunteers" && rows.map((v) => <VolunteerCard key={v.id} v={v} />)}
      </div>
    </div>
  );
}
